import React, { useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { FormProvider, RHFFileField, RHFRadioGroup, RHFSelectField, RHFTextField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { escalationArray, generateRandom10DigitNumber } from "../../helpers/UtilityHelper";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { apiConstants, post, postGeneric } from "../../common/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useEffect } from "react";
import RHFTextArea from "../../common/hook-form/RHFTextArea";
import { getMerchant, getToken } from "../../helpers/ReduxHelpers";
import { useSnackbar } from "notistack";
import jwt_decode from "jwt-decode"
import SegmentOutlinedIcon from '@mui/icons-material/SegmentOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, LIGHT_BG, PRIMARY_BLUE, WARNING, withOpacity } from '../../styles/foundation/colors';

// Section card — icon badge + title/subtitle header over a bordered white card, extending the
// same visual language used across the redesigned list/detail screens to this form.
const cardSx = { border: `1px solid ${BORDER}`, borderRadius: '12px', bgcolor: WHITE, overflow: 'hidden', mb: 2 };
const cardHeadSx = { display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: LIGHT_BG };
const cardIconSx = { width: 30, height: 30, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: withOpacity(PRIMARY_BLUE, 0.1), color: PRIMARY_BLUE, flexShrink: 0, '& svg': { fontSize: 17 } };

const SectionCard = ({ icon, title, subtitle, children }) => (
    <Box sx={cardSx}>
        <Box sx={cardHeadSx}>
            <Box sx={cardIconSx}>{icon}</Box>
            <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>{title}</Typography>
                {subtitle && <Typography sx={{ fontFamily: FONT, fontSize: '10.5px', color: SUBTLE, mt: 0.1 }}>{subtitle}</Typography>}
            </Box>
        </Box>
        <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
);

const CreateTicket = ({ setFiltersChange = null, handleOpen, setTicketId, awb = null, statusList }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [formFields, setFormFields] = useState([])
    const [dueDate, setDueDate] = useState("")
    const [frDueDate, setFrDueDate] = useState("")
    // const { userInfo } = useSelector((state) => state.user);
    const userToken = jwt_decode(getToken());
    const merchantDetail = getMerchant();

    const { enqueueSnackbar } = useSnackbar();

    const formElementNames = formFields?.map((val) => ({
        name: val.name,
        isMandatory: val.isMandatory,
        type: val.type
    })) ?? []

    const createValidationSchema = (formElementNames) => {
        const schemaObject = {};
        formElementNames?.forEach((element, index) => {
            const key = element.type === 'file' ? `${element.name}_${index}` : element.name;
            if (element.isMandatory === 'Y' && element.type === 'file') {
                schemaObject[key] = Yup.mixed().required(`Please upload file`);
            } else if (element.isMandatory === 'Y' && element.type !== 'file') {
                schemaObject[key] = Yup.string().required(`Please provide ${element.name}`);
            } else if (key === "add_email") {
                schemaObject['add_email'] = Yup.string().test(
                    'valid-emails',
                    'Invalid email address',
                    (value) => {
                        if (!value) return true;
                        const emails = value.split(',').map(email => email.trim());
                        return emails.every(email => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email));
                    }
                );
            }
        });
        return Yup.object().shape(schemaObject);
    };

    const validationSchema = createValidationSchema([
        ...formElementNames,
        { name: 'category', isMandatory: 'Y' },
        { name: 'subcategory', isMandatory: 'Y' },
        { name: 'add_email', isMandatory: 'N' },
    ]);


    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            category: '',
            subcategory: '',
            add_email: '',
        },
        shouldUnregister: true
    });

    const { handleSubmit, reset, watch, setValue } = methods;

    const selectedCategory = watch("category");
    const selectedSubcategory = watch("subcategory");
    const data = escalationArray.filter((val) => val.categoryTitle);
    const categoryOptions = _.orderBy(data, "name").map((val) => ({
        value: val.categoryTitle,
        label: val.categoryTitle,
    }));

    const subcategoryOptions =
        selectedCategory &&
        data.find((val) => val.categoryTitle === selectedCategory)?.subCategories.map((subCat) => ({
            value: subCat.subCatTitle,
            label: subCat.subCatTitle,
        }));

    useEffect(() => {
        if (selectedSubcategory) {
            const formData =
                selectedCategory &&
                data.find((val) => val.categoryTitle === selectedCategory)?.subCategories.find(
                    (subCat) => subCat.subCatTitle === selectedSubcategory
                );
            const newFormFields = formData?.formElements || [];
            setDueDate(formData?.due)
            setFrDueDate(formData?.frDue)
            setFormFields(newFormFields);
            reset();
            newFormFields.forEach((elem, index) => {
                if (elem.type !== "file") {
                    setValue(elem.name, '');
                } else {
                    setValue(`${elem.name}_${index}`, null);
                }
            });
            setValue("category", selectedCategory);
            setValue("subcategory", selectedSubcategory);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubcategory, selectedCategory]);


    const buildFormData = (data, awbNumber, cpId) => {
        let formData = new FormData();
        let descriptionString = data.description ? data.description : 'No Description';

        const ccEmails = data.add_email ? data.add_email.split(',') : [];
        ccEmails.forEach(email => {
            formData.append('cc_emails[]', email.trim());
        });

        for (const key in data) {
            if (key !== 'email' && key !== 'subject' && key !== 'status' && key !== 'priority' &&
                key !== 'category' && key !== 'awbNumber' && key !== 'merchantId' &&
                key !== 'subcategory' && key !== 'description' && !key.startsWith('uploadedFile_')) {
                descriptionString += `|${key} ${data[key]}`;
            }
        }
        formData.append('description', descriptionString);
        for (const key in data) {
            if (key.startsWith('uploadedFile_') && data[key] !== undefined && data[key] !== null) {
                formData.append('attachments', data[key][0]);
            }
        }
        formData.append('email', userToken?.name);
        formData.append('name', userToken?.fullname);
        formData.append('custom_fields[cf_user_email]', userToken?.name);
        formData.append('custom_fields[cf_user_name]', userToken?.fullname);
        formData.append('subject', `${data.category} / ${data.subcategory} / ${awbNumber ? awbNumber : generateRandom10DigitNumber()}`);
        formData.append('status', 2);
        formData.append('priority', 2);
        formData.append('frDueBy', frDueDate ? frDueDate : 2);
        formData.append('dueBy', dueDate ? dueDate : 24);
        formData.append('custom_fields[cf_category]', data.category);
        if (awbNumber) {
            formData.append('custom_fields[cf_awb_number387695]', awbNumber);
        }
        if (cpId && cpId !== null) {
            formData.append('custom_fields[cf_cp_id]', cpId);
        }
        formData.append('custom_fields[cf_sub_category]', data.subcategory);
        formData.append('custom_fields[cf_merchant_id]', merchantDetail?.id);
        formData.append('custom_fields[cf_merchant_name]', merchantDetail?.name);
        formData.append('tags', data?.merchant ? data?.merchant.toString().substring(0, 32) : '-');
        cpId && (formData.append('tags', cpId?.toString().substring(0, 32)));
        return formData;
    };


    const checkExistingTicket = async (awbNumber, category, subcategory) => {
        const statusListIds = statusList?.map(status => status.id).filter(status => status !== 4 && status !== 5);
        try {
            const request = {
                awbnumber_list: [awbNumber],
                category_list: [category],
                subcategory_list: [subcategory],
                status_list: statusListIds, // Open and Pending status
                page: 1,
                limit: 1
            };
            const response = await postGeneric(`${apiConstants.FILTER_FRESHDESK}`, request);
            const parsedRes = JSON.parse(response.data?.responseObj);
            return parsedRes?.total > 0;
        } catch (error) {
            console.error('Error checking existing ticket:', error);
            return false;
        }
    };

    const onSubmit = async (data) => {
        setIsLoading(true)
        try {
            if (data.awbNumber) {
                const awbNumbers = data.awbNumber.split(',').map(num => num.trim());
                const uniqueAWBNumbers = Array.from(new Set(awbNumbers));
                // const merId = merchantId.merchantOid
                const merId = merchantDetail?.id
                for (const awb of uniqueAWBNumbers) {
                    const existingTicket = await checkExistingTicket(awb, data.category, data.subcategory);
                    if (existingTicket) {
                        enqueueSnackbar(`Ticket already exists for AWB ${awb} with category "${data.category}" and subcategory "${data.subcategory}"`, { variant: 'error' });
                        continue;
                    }
                    let d = await fetchAWBData(awb, merId)
                    if (!d || d === null) {
                        enqueueSnackbar(`AWB Number ${awb} invalid or doesnt belong to this merchant`, { variant: 'error' })
                        setIsLoading(false);
                    } else {
                        let cpId = d;
                        const formData = buildFormData(data, awb, cpId);
                        submitForm(formData);
                    }
                }
            } else {
                const formData = buildFormData(data, null);
                submitForm(formData);
            }
        } catch (err) {
            toast("Some error occurred!", { type: "error" });
            setIsLoading(false);
            console.log(err);
        }
    };

    const submitForm = async (formData) => {
        try {
            const res = await post(`${apiConstants.BASE_LOCAL}${apiConstants.CREATE_FRESHDESK}`, formData);
            if (res.data?.status === "ERROR") {
                enqueueSnackbar(res.data?.responseObj || "Some error occurred!", { variant: 'error' })
                setIsLoading(false)
            } else {
                const parsedData = JSON.parse(res.data?.responseObj);
                setTicketId(parsedData?.id);
                handleOpen();
                reset();
                setFormFields([]);
                if (setFiltersChange) {
                    setFiltersChange(filterChange => filterChange + 1);
                }
            }
            setIsLoading(false);
        } catch (err) {
            enqueueSnackbar("Some error occurred!", { variant: 'error' })
            setIsLoading(false);
            console.log(err);
        }
    };

    const fetchAWBData = async (awb, merchantSelectId) => {
        try {
            const andfilter = [
                {
                    "field": "awbNumber",
                    "operator": "eq",
                    "value": awb.trim()
                }
            ];
            const includes = "merchantDetail,courierDetail";
            let d = { "andfilter": andfilter, "includes": includes };
            const res = await post(`${apiConstants.BASE_LOCAL}${apiConstants.ORDER_LISTS}`, d); // Wait for the post request to complete
            if (res.status !== 200 || res?.data?.count === 0) {
                return null;
            } else {
                let data = res.data?.data[0];
                let merchantFromData = data?.merchantDetail.key;
                if (merchantFromData !== merchantSelectId) {
                    return null;
                } else {
                    return data?.courierDetail?.prozoId;
                }
            }
        } catch (error) {
            console.error('Error fetching AWB data:', error);
            return null; // Return null if there's an issue fetching the AWB data
        }
    };

    // alignSelf:'stretch' on the outer Box — the SideDrawer wraps children in a flex column with
    // alignItems:'center', which otherwise shrinks this whole subtree to its content width and
    // centres it, reading as a left/right margin. The inner Box bleeds over the drawer's own
    // 30px/16px content padding (mt:2 + py:'10px') so the section cards fill it edge-to-edge.
    return (
        <Box sx={{ width: '100%', alignSelf: 'stretch' }}>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mx: '-30px', mt: '-26px', px: '30px', pt: 3, pb: 1 }}>
            <SectionCard icon={<SegmentOutlinedIcon />} title="Classification" subtitle="What is this ticket about?">
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <RHFSelectField label="Category" name="category" options={categoryOptions} width="100%" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {selectedCategory && (
                            <RHFSelectField label="Subcategory" name="subcategory" options={subcategoryOptions} width="100%" />
                        )}
                    </Grid>
                </Grid>

                {selectedCategory && selectedSubcategory && (
                    <Box sx={{
                        mt: 2, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '8px',
                        bgcolor: withOpacity(WARNING, 0.1), border: `1px solid ${withOpacity(WARNING, 0.3)}`,
                    }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: '#B45309' }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.semibold, color: '#B45309' }}>
                            First response in {frDueDate || 2}h · Resolution by {dueDate || 24}h
                        </Typography>
                    </Box>
                )}
            </SectionCard>

            {formFields?.length > 0 && (
                <SectionCard icon={<AssignmentOutlinedIcon />} title="Ticket Details" subtitle="Tell us more so we can resolve this faster">
                    <Grid container spacing={2.5}>
                        <FormFields formFields={formFields} awb={awb} setValue={setValue} />
                    </Grid>
                </SectionCard>
            )}

            {selectedCategory && selectedSubcategory && (
                <SectionCard icon={<MailOutlineIcon />} title="Notifications" subtitle="Optional — keep others in the loop">
                    <RHFTextField label="Additional Emails (Comma Separated)" name="add_email" fullWidth />
                </SectionCard>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <LoadingButton loading={isLoading} type="submit" disabled={formFields?.length === 0} variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.bold, borderRadius: '10px', px: 2.75, bgcolor: PRIMARY_BLUE, boxShadow: `0 6px 16px ${withOpacity(PRIMARY_BLUE, 0.35)}`, '&:hover': { bgcolor: PRIMARY_BLUE } }}>
                    Create Ticket
                </LoadingButton>
                <Button variant="outlined" disabled={formFields?.length === 0} onClick={() => {
                    reset()
                    setFormFields([])
                }} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, borderRadius: '10px', color: SUBTLE, borderColor: BORDER }}>
                    Reset
                </Button>
            </Box>
            </Box>
        </FormProvider>
        </Box>
    );
};

export default CreateTicket


const FormFields = ({ formFields, awb, setValue }) => {
    useEffect(() => {
        if (awb !== null) {
            formFields?.forEach(element => {
                if (element.name === 'awbNumber') {
                    setValue(element.name, awb);
                }
            });
        }
    }, [awb, formFields, setValue]);


    return (
        <>
            {formFields?.map((formElement, index) => (
                <React.Fragment key={`${formElement.subcategory}_${index}`}>
                    {formElement.type === "text" && (
                        <Grid item key={index} xs={12} md={6}>
                            <>
                                <RHFTextField
                                    label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                    name={formElement.name}
                                    fullWidth
                                    disabled={formElement.name === "awbNumber" && awb !== null}
                                />
                            </>
                        </Grid>
                    )}
                    {formElement.type === "textarea" && (
                        <Grid item key={index} xs={12} md={12}>
                            <>
                                <RHFTextArea
                                    // label={formElement.title}
                                    label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                    name={formElement.name}
                                    rows={4}
                                    fullWidth
                                />
                            </>
                        </Grid>
                    )}
                    {formElement.type === "file" && (
                        <Grid item key={index} xs={12} md={6}>
                            <RHFFileField
                                type="file"
                                label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                name={`${formElement.name}_${index}`}
                            />
                        </Grid>
                    )}
                    {formElement.type === "radio" && (
                        <Grid item key={index} xs={12} md={6}>
                            <RHFRadioGroup
                                type="radio"
                                label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                name={formElement.name}
                                options={formElement.options.map((val) => {
                                    return {
                                        label: val, value: val
                                    }
                                })}
                            />
                        </Grid>
                    )}
                </React.Fragment>
            ))
            }
        </>
    )
}
export { FormFields }
